/**
 * Maps promotion candidate claims (Task 39) → Work Queue claim rows (CT-5095).
 *
 * The UI `ClaimRow` expects { id, text, confidence (0–1), conflict?, sourceA?, sourceB? }.
 * Decision keys use `candidateClaimUuid` when present (stable for API writes).
 */

/** First 8 chars uppercased — compact display id when claimCode is absent. */
function shortRef(uuid) {
  return uuid ? String(uuid).slice(0, 8).toUpperCase() : null;
}

/** Zero-padded CLM-00N fallback matching the prototype fixtures. */
function syntheticClaimId(index) {
  return `CLM-${String(index + 1).padStart(3, '0')}`;
}

function normalizeConfidence(raw) {
  if (raw == null) return null;
  const numeric = Number(raw);
  if (!Number.isFinite(numeric)) return null;
  return numeric > 1 ? numeric / 100 : numeric;
}

function isConflictClaim(cc = {}) {
  const status = String(cc.status ?? '').toLowerCase();
  if (status === 'conflict') return true;
  const count = cc.conflictCount ?? cc.conflict_count;
  return count != null && Number(count) > 0;
}

/**
 * Pull conflict compare sides from metadata when the API sends them.
 * The sandbox spec does not yet guarantee sourceA/sourceB — the block renders only
 * when both sides are present.
 */
function mapConflictSources(cc = {}) {
  const meta = cc.metadata ?? cc.claimJson ?? {};
  const sourceA = meta.sourceA ?? meta.source_a ?? cc.sourceA ?? cc.source_a ?? null;
  const sourceB = meta.sourceB ?? meta.source_b ?? cc.sourceB ?? cc.source_b ?? null;
  if (!sourceA || !sourceB) return { sourceA: null, sourceB: null };
  return { sourceA, sourceB };
}

/**
 * @param {Object} cc - CandidateClaimItem
 * @param {number} index - row index for synthetic id fallback
 * @returns {Object} UI claim row
 */
export function mapCandidateClaimToUi(cc = {}, index = 0) {
  const candidateClaimUuid = cc.candidateClaimUuid ?? cc.candidate_claim_uuid ?? null;
  const claimCode = cc.claimCode ?? cc.claim_code ?? null;
  const displayId = claimCode ?? shortRef(cc.candidateClaimUuid ?? cc.candidate_claim_uuid) ?? syntheticClaimId(index);
  const conflict = isConflictClaim(cc);
  const { sourceA, sourceB } = conflict ? mapConflictSources(cc) : { sourceA: null, sourceB: null };

  return {
    id: candidateClaimUuid ?? displayId,
    displayId,
    candidateClaimUuid,
    claimUuid: cc.claimUuid ?? cc.claim_uuid ?? null,
    confidence: normalizeConfidence(cc.confidence),
    text: (cc.claimText ?? cc.claim_text ?? cc.title ?? '').trim(),
    conflict,
    reviewStatus: cc.reviewStatus ?? cc.review_status ?? 'pending_review',
    sourceA,
    sourceB,
  };
}

/**
 * @param {Object[]} rows - CandidateClaimItem[]
 * @returns {Object[]} UI claim rows
 */
export function mapPromotionClaimsToUi(rows = []) {
  return rows.map((row, index) => mapCandidateClaimToUi(row, index));
}

/** Count claims whose sandbox status marks a knowledge conflict. */
export function countConflictClaims(rows = []) {
  return rows.filter((row) => isConflictClaim(row)).length;
}
